import React from "react"
import { useTranslation } from "react-i18next"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { IRT } from "constants/index"
import { PlanInfo } from "types/order.types"
import { rialToToman } from "utils/generalUtils"
import Divider from "components/atoms/Divider"

type CheckoutDetailSectionProps = {
  totalAmount: number;
  planInfo: PlanInfo;
}

export default function CheckoutDetailSection(props: CheckoutDetailSectionProps) {
  const { totalAmount, planInfo } = props

  const { t } = useTranslation()

  return (
    <div>
      <Title>{t("checkout.paymentDetail")}</Title>
      <Container>
        <PriceRow name={planInfo.name} price={planInfo.price}/>
        <Divider isFullWidth={true}/>
        <PriceRow name={t("checkout.total")} price={totalAmount} isTotalPrice={true}/>
      </Container>
    </div>
  )
}

type PriceRowProps = {
  name: string;
  price: number;
  isTotalPrice?: boolean;
}

function PriceRow(props: PriceRowProps) {
  const { name, price, isTotalPrice = false } = props

  const { t } = useTranslation()

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <PriceLabel bold={isTotalPrice ? 1 : 0}>{name}</PriceLabel>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap="4px">
        <Price className="farsi-digits" bold={isTotalPrice ? 1 : 0}>{rialToToman(price)}</Price>
        <Currency>{t(`currency.${IRT}`)}</Currency>
      </Stack>
    </Stack>
  )
}

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 500,
  color: theme.palette.gray["14"],
  marginTop: "8px",
  marginBottom: "12px"
}))

const Container = styled(Stack)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: `0 0 7px 0 ${theme?.palette.gray["6"]}`,
  backgroundColor: "#FFFFFF",
  gap: "8px",
  padding: "16px 12px"
}))

type TextProps = {
  theme?: Theme;
  bold: 1 | 0;
}

const PriceLabel = styled("span")(({ theme, bold }: TextProps) => ({
  fontSize: bold ? "1.5rem" : "1.3rem",
  color: bold ? theme?.palette.gray["16"] : theme?.palette.gray["12"],
  fontWeight: bold ? 700 : 400
}))

const Price = styled("span")(({ theme, bold }: TextProps) => ({
  fontSize: bold ? "1.9rem" : "1.5rem",
  color: bold ? theme?.palette.gray["16"] : theme?.palette.gray["15"],
  fontWeight: bold ? 700 : 400
}))

const Currency = styled("span")(({ theme }) => ({
  fontSize: "1.1rem",
  color: theme.palette.gray["12"]
}))
